import React, { useState, useEffect, useCallback } from 'react';
import { Check, X, Loader2, Copy, ExternalLink, Link } from 'lucide-react';
import { expertDashboardApi, getErrorMessage, SlugCheckResponse } from '../../../lib/http';
import { ExpertProfile } from '../../../types';
import toast from 'react-hot-toast';

interface PublicLinkSettingsProps {
  profile: ExpertProfile;
  onUpdate: (profile: ExpertProfile) => void;
}

export const PublicLinkSettings: React.FC<PublicLinkSettingsProps> = ({
  profile,
  onUpdate,
}) => {
  const [slug, setSlug] = useState(profile.slug || '');
  const [isChecking, setIsChecking] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [checkResult, setCheckResult] = useState<SlugCheckResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Debounced slug check
  useEffect(() => {
    const normalizedSlug = slug.toLowerCase().trim();

    // Clear results if empty or same as current
    if (!normalizedSlug || normalizedSlug === profile.slug) {
      setCheckResult(null);
      setError(null);
      return;
    }

    // Validate format before API call
    if (normalizedSlug.length < 3) {
      setCheckResult({ available: false, error: 'Slug must be at least 3 characters' });
      return;
    }

    if (!/^[a-z][a-z0-9_]*$/.test(normalizedSlug)) {
      setCheckResult({
        available: false,
        error: 'Must start with a letter and contain only lowercase letters, numbers, and underscores'
      });
      return;
    }

    const timeoutId = setTimeout(async () => {
      setIsChecking(true);
      setError(null);
      try {
        const response = await expertDashboardApi.profile.checkSlug(normalizedSlug);
        setCheckResult(response.data);
      } catch (err) {
        setError(getErrorMessage(err));
        setCheckResult(null);
      } finally {
        setIsChecking(false);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [slug, profile.slug]);

  const handleSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '');
    setSlug(value);
  };

  const handleSave = async () => {
    const normalizedSlug = slug.toLowerCase().trim();

    // Allow saving empty slug (to clear it)
    if (normalizedSlug && checkResult && !checkResult.available) {
      toast.error('Please choose an available slug');
      return;
    }

    setIsSaving(true);
    setError(null);
    try {
      const response = await expertDashboardApi.profile.update({
        slug: normalizedSlug || undefined,
      });
      onUpdate(response.data);
      toast.success(normalizedSlug ? 'Public link saved!' : 'Public link removed');
      setCheckResult(null);
    } catch (err) {
      setError(getErrorMessage(err));
      toast.error(getErrorMessage(err));
    } finally {
      setIsSaving(false);
    }
  };

  const handleCopyLink = useCallback(() => {
    if (profile.public_url) {
      navigator.clipboard.writeText(profile.public_url);
      toast.success('Link copied to clipboard!');
    }
  }, [profile.public_url]);

  const handleSuggestionClick = (suggestion: string) => {
    setSlug(suggestion);
  };

  const hasChanges = slug.toLowerCase().trim() !== (profile.slug || '');
  const canSave = hasChanges && (!slug || (checkResult?.available === true));

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-pink-100 rounded-lg">
          <Link className="w-5 h-5 text-pink-600" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Public Booking Link</h3>
          <p className="text-sm text-gray-500">
            Create a custom link for clients to book with you
          </p>
        </div>
      </div>

      {/* Current Public URL */}
      {profile.public_url && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-green-800 mb-1">Your booking link:</p>
              <p className="text-sm text-green-700 truncate">{profile.public_url}</p>
            </div>
            <div className="flex gap-2 ml-4">
              <button
                onClick={handleCopyLink}
                className="p-2 text-green-700 hover:bg-green-100 rounded-lg transition-colors"
                title="Copy link"
              >
                <Copy className="w-4 h-4" />
              </button>
              <a
                href={profile.public_url}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 text-green-700 hover:bg-green-100 rounded-lg transition-colors"
                title="Open link"
              >
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Slug Input */}
      <div className="space-y-2">
        <label htmlFor="slug" className="block text-sm font-medium text-gray-700">
          Custom URL slug
        </label>
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
                /book/
              </span>
              <input
                type="text"
                id="slug"
                value={slug}
                onChange={handleSlugChange}
                placeholder="yourname"
                maxLength={30}
                className={`w-full pl-14 pr-10 py-2 border rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500 ${
                  checkResult?.available === false
                    ? 'border-red-300 bg-red-50'
                    : checkResult?.available === true
                    ? 'border-green-300 bg-green-50'
                    : 'border-gray-300'
                }`}
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                {isChecking && <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />}
                {!isChecking && checkResult?.available === true && (
                  <Check className="w-4 h-4 text-green-500" />
                )}
                {!isChecking && checkResult?.available === false && (
                  <X className="w-4 h-4 text-red-500" />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Feedback Messages */}
        {checkResult?.available === false && checkResult.error && (
          <p className="text-sm text-red-600">{checkResult.error}</p>
        )}

        {/* Suggestions */}
        {checkResult?.suggestions && checkResult.suggestions.length > 0 && (
          <div className="mt-2">
            <p className="text-sm text-gray-500 mb-1">Try these instead:</p>
            <div className="flex flex-wrap gap-2">
              {checkResult.suggestions.map((suggestion) => (
                <button
                  key={suggestion}
                  onClick={() => handleSuggestionClick(suggestion)}
                  className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full transition-colors"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        )}

        {error && <p className="text-sm text-red-600">{error}</p>}

        <p className="text-xs text-gray-500">
          3-30 characters. Lowercase letters, numbers, and underscores only. Must start with a letter.
        </p>
      </div>

      {/* Save Button */}
      <div className="mt-6 flex justify-end">
        <button
          onClick={handleSave}
          disabled={!canSave || isSaving}
          className="px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
        >
          {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
          {isSaving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  );
};

export default PublicLinkSettings;
